import React from "react";
import { motion } from "framer-motion";
import { TrendingUp, ArrowUpRight } from "lucide-react";
import { TIDS } from "@/lib/testIds";

// NOTE: sample case studies — clearly editable, swap with real client results.
const cases = [
  {
    practice: "Bright Smile Dental",
    location: "Austin, TX",
    tag: "General + Cosmetic",
    image:
      "https://images.pexels.com/photos/3779709/pexels-photo-3779709.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    headline: "From 12 to 71 new patients a month in 120 days.",
    metrics: [
      { k: "New patients / mo", v: "+491%" },
      { k: "Cost per booking", v: "-38%" },
      { k: "ROAS", v: "7.2×" },
    ],
  },
  {
    practice: "Meridian Implant Studio",
    location: "Toronto, CA",
    tag: "Implants",
    image:
      "https://images.pexels.com/photos/4270092/pexels-photo-4270092.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    headline: "$412K in attributed implant revenue in two quarters.",
    metrics: [
      { k: "Attributed revenue", v: "$412K" },
      { k: "Qualified consults", v: "+264%" },
      { k: "Show-up rate", v: "84%" },
    ],
  },
  {
    practice: "Coastline Orthodontics",
    location: "Jersey City, NJ",
    tag: "Orthodontics",
    image:
      "https://images.pexels.com/photos/305566/pexels-photo-305566.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    headline: "Filled an empty associate schedule in six weeks flat.",
    metrics: [
      { k: "Chair utilization", v: "98%" },
      { k: "New starts / mo", v: "+58" },
      { k: "Ad payback", v: "21 days" },
    ],
  },
];

const card = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

export default function CaseStudies() {
  return (
    <section id="case-studies" className="py-24 md:py-32 relative">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16"
        >
          <div className="md:col-span-7">
            <div className="font-mono-accent text-[11px] uppercase tracking-[0.25em] text-blue-400 mb-4">
              / Case studies
            </div>
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl tracking-tighter font-medium text-white leading-[1.05]">
              Receipts, not promises.
            </h2>
          </div>
          <div className="md:col-span-5 flex items-end">
            <p className="text-lg text-white/55 leading-relaxed">
              Real practices, real numbers, tracked from ad-click to booked
              appointment. Your results will be your own.
            </p>
          </div>
        </motion.div>

        <div className="space-y-6">
          {cases.map((c, i) => (
            <motion.article
              key={c.practice}
              data-testid={TIDS.caseStudyCard(i + 1)}
              variants={card}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
              className="group grid grid-cols-1 lg:grid-cols-12 gap-0 rounded-3xl overflow-hidden border border-white/10 bg-white/[0.03] hover:border-white/25 transition-colors duration-300"
            >
              {/* image — clipped frame */}
              <div className={`lg:col-span-5 relative overflow-hidden min-h-[260px] ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <img
                  src={c.image}
                  alt={c.practice}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#070B14]/80 via-[#070B14]/10 to-transparent" />
                <div className="absolute top-5 left-5 inline-flex items-center gap-2 rounded-full bg-black/40 backdrop-blur-md border border-white/15 px-3 py-1">
                  <span className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-white/80">
                    {c.tag}
                  </span>
                </div>
              </div>

              {/* content */}
              <div className="lg:col-span-7 p-8 md:p-12 flex flex-col justify-center">
                <div className="flex items-center gap-2 text-sm text-white/50">
                  <span className="text-white font-medium">{c.practice}</span>
                  <span>·</span>
                  <span>{c.location}</span>
                </div>
                <h3 className="mt-3 font-display text-2xl md:text-3xl tracking-tight text-white leading-snug max-w-xl">
                  {c.headline}
                </h3>

                <div className="mt-8 grid grid-cols-3 gap-4">
                  {c.metrics.map((m) => (
                    <div key={m.k} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
                      <div className="font-display text-2xl md:text-3xl font-semibold text-amber-400">
                        {m.v}
                      </div>
                      <div className="mt-1 text-[11px] text-white/50 leading-tight">{m.k}</div>
                    </div>
                  ))}
                </div>

                <div className="mt-8 inline-flex items-center gap-2 text-sm text-white/70 group-hover:text-amber-400 transition-colors">
                  <TrendingUp className="w-4 h-4" />
                  <span>Full breakdown on your strategy call</span>
                  <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
